import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";

import {
  useGetComprovanteMatriculaMutation,
  useGetCurriculoMutation,
} from "shared/features/api/formulario/formularioSlice";
import {
  useUploadComprovanteMatriculaMutation,
  useUploadCurriculoMutation,
} from "shared/features/api/inscricao/inscricaoSlice";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  LinearProgress,
} from "@mui/material";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import "react-medium-image-zoom/dist/styles.css";
import { IElementos } from "shared/features/api/inscricao/types";
import { EtapaAcordeaoItem } from "components/avaliacao/EtapaAccordionItem";
import { toast } from "react-toastify";

interface CurriculoContainerProps {
  inscricao: IElementos
}

const columns = [
  {
    field: "pergunta",
    headerName: "Pergunta",
    minWidth: 180,
    maxWidth: 550,
    flex: 1,
  },
  {
    field: "resposta",
    headerName: "Resposta",
    minWidth: 230,
    flex: 1,
  },
];

export const CurriculoContainer: React.FC<CurriculoContainerProps> = ({ inscricao }) => {
    const [curriculo, setCurriculo] = useState<string | null>(null);
    const [comprovanteMatricula, setComprovanteMatricula] = useState<string | null>(null);
    const [comprovanteFormatadoPDF, setComprovanteFormatadoPDF] = useState<string>("")
    const defaultLayoutPluginInstance = defaultLayoutPlugin();
    const [formattedCandidatePdf, setFormattedCandidatePdf] =
      useState<string>("");
    const [uploadingCurriculo, setUploadingCurriculo] = useState(false);
    const [uploadingComprovante, setUploadingComprovante] = useState(false);

    const [open, setOpen] = React.useState(false);

    const handleClose = () => {
      setOpen(false);
    };

    const [value, setValue] = React.useState("1");
    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
      setValue(newValue);
    };

    const [getCurriculo, {isLoading: carregandoCurriculo}] = useGetCurriculoMutation();
    const [getComprovanteMatricula, {isLoading: carregandoComprovanteMatricula}] = useGetComprovanteMatriculaMutation();
    const [uploadCurriculo] = useUploadCurriculoMutation();
    const [uploadComprovanteMatricula] = useUploadComprovanteMatriculaMutation();
    useEffect(() => {
      if (inscricao?.idInscricao){
        getCurriculo(inscricao.idInscricao)
          .unwrap()
          .then((e) => {
            setCurriculo(e);
          })
          .catch((err) => {
            console.log(err);
          });

        getComprovanteMatricula(inscricao.idInscricao)
        .unwrap()
          .then((response) => {
            setComprovanteMatricula(response);
          })
          .catch((err) => {
            console.error(err);
          });
      }
    }, [inscricao]);

    const base64toBlob = (data: string) => {
      if (!data) {
        throw new Error("PDF_EMPTY_DATA");
      }
      const byteString = atob(data);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: "application/pdf" });
    };

    useEffect(() => {
      try {
        if (curriculo) {
          const blobCurriculo = base64toBlob(curriculo);
          const urlCurriculo = URL.createObjectURL(blobCurriculo);
          setFormattedCandidatePdf(urlCurriculo);
        } else {
          setFormattedCandidatePdf("");
        }
      } catch {
        setFormattedCandidatePdf("");
      }

      try {
        if (comprovanteMatricula) {
          const blobComprovanteMatricula = base64toBlob(comprovanteMatricula);
          const urlComprovanteMatricula = URL.createObjectURL(blobComprovanteMatricula);
          setComprovanteFormatadoPDF(urlComprovanteMatricula);
        } else {
          setComprovanteFormatadoPDF("");
        }
      } catch {
        setComprovanteFormatadoPDF("");
      }
    }, [curriculo, comprovanteMatricula]);

    const reloadCurriculo = async () => {
      const response = await getCurriculo(inscricao.idInscricao).unwrap();
      setCurriculo(response);
    };

    const reloadComprovante = async () => {
      const response = await getComprovanteMatricula(inscricao.idInscricao).unwrap();
      setComprovanteMatricula(response);
    };

    const handleUploadCurriculo = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        setUploadingCurriculo(true);
        const formData = new FormData();
        formData.append("file", file);
        await uploadCurriculo({ idInscricao: inscricao.idInscricao, file: formData }).unwrap();
        await reloadCurriculo();
        toast.success("Currículo enviado com sucesso.");
      } catch {
        toast.error("Não foi possível enviar o currículo.");
      } finally {
        setUploadingCurriculo(false);
        event.target.value = "";
      }
    };

    const handleUploadComprovante = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        setUploadingComprovante(true);
        const formData = new FormData();
        formData.append("file", file);
        await uploadComprovanteMatricula({ idInscricao: inscricao.idInscricao, file: formData }).unwrap();
        await reloadComprovante();
        toast.success("Comprovante de matrícula enviado com sucesso.");
      } catch {
        toast.error("Não foi possível enviar o comprovante de matrícula.");
      } finally {
        setUploadingComprovante(false);
        event.target.value = "";
      }
    };

    const renderUploadFallback = (tipo: "curriculo" | "comprovante") => (
      <Box sx={{ p: 2 }}>
        <FormHelperText error>
          Não foi possível exibir o PDF (estrutura inválida ou arquivo indisponível).
        </FormHelperText>
        <Button
          component="label"
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
          disabled={uploadingCurriculo || uploadingComprovante}
        >
          {tipo === "curriculo" ? "Upload currículo" : "Upload comprovante"}
          <input
            hidden
            type="file"
            accept="application/pdf"
            onChange={tipo === "curriculo" ? handleUploadCurriculo : handleUploadComprovante}
          />
        </Button>
      </Box>
    );

    function formatBooleanText(texto: string) {
      if(texto === "T") {
        return "Sim"
      }

      return "Não"
    }
    

    const rows = [
      {
        id: 1,
        tipo: "Pessoal",
        pergunta: "Nome",
        resposta: inscricao?.candidato.nome,
      },
      {
        id: 2,
        tipo: "Pessoal",
        pergunta: "Email",
        resposta: inscricao?.candidato.email,
      },
      {
        id: 3,
        tipo: "Pessoal",
        pergunta: "Telefone",
        resposta: inscricao?.candidato.telefone,
      },
      {
        id: 4,
        tipo: "Pessoal",
        pergunta: "Cpf",
        resposta: inscricao?.candidato.cpf,
      },
      {
        id: 5,
        tipo: "Pessoal",
        pergunta: "Data de nascimento",
        resposta: inscricao?.candidato.dataNascimento,
      },
      {
        id: 6,
        tipo: "Pessoal",
        pergunta: "Estado",
        resposta: inscricao?.candidato.estado,
      },
      {
        id: 7,
        tipo: "Pessoal",
        pergunta: "Cidade",
        resposta: inscricao?.candidato.cidade,
      },
      {
        id: 8,
        tipo: "Pessoal",
        pergunta: "Neurodiversidade",
        resposta: inscricao?.candidato.neurodiversidade,
      },
      {
        id: 9,
        tipo: "Pessoal",
        pergunta: "Gênero",
        resposta: inscricao?.candidato.genero,
      },
      {
        id: 10,
        tipo: "Pessoal",
        pergunta: "Deficiência",
        resposta: inscricao?.candidato.pcd,
      },
      {
        id: 11,
        tipo: "Acadêmico",
        pergunta: "Instituição",
        resposta: inscricao?.instituicao,
      },
      {
        id: 12,
        tipo: "Acadêmico",
        pergunta: "Curso",
        resposta: inscricao?.curso,
      },
      {
        id: 13,
        tipo: "Acadêmico",
        pergunta: "Graduação",
        resposta: inscricao?.tipoGraduacao ?? "Não fornecido"
      },
      {
        id: 14,
        tipo: "Acadêmico",
        pergunta: "Turno",
        resposta: inscricao?.turno,
      },
      {
        id: 15,
        tipo: "Acadêmico",
        pergunta: "Quantidade de semestres",
        resposta: inscricao?.qtdSemestres ?? "Não fornecido"
      },
      {
        id: 16,
        tipo: "Acadêmico",
        pergunta: "Semestre atual",
        resposta: inscricao?.semestreAtual ?? "Não fornecido"
      },
      {
        id: 17,
        tipo: "Acadêmico",
        pergunta: "Nível de inglês",
        resposta: inscricao?.ingles,
      },
      {
        id: 18,
        tipo: "Acadêmico",
        pergunta: "Nível de espanhol",
        resposta: inscricao?.espanhol,
      },
      {
        id: 19,
        tipo: "Outros",
        pergunta: "Motivo por qual se interessou pela área e DBC",
        resposta: inscricao?.resposta,
      },
      {
        id: 20,
        tipo: "Outros",
        pergunta: "Algo importante",
        resposta: inscricao?.importancia,
      },
      {
        id: 21,
        tipo: "Outros",
        pergunta: "Conhecimentos necessários para a prova",
        resposta: formatBooleanText(inscricao?.prova as string) ?? "Não fornecido",
      },
      {
        id: 22,
        tipo: "Outros",
        pergunta: "Interesse em Efetivação",
        resposta: formatBooleanText(inscricao?.efetivacao as string) ?? "Não fornecido",
      },
      {
        id: 23,
        tipo: "Outros",
        pergunta: "Disponibilidade para aulas",
        resposta: formatBooleanText(inscricao?.disponibilidade as string) ?? "Não fornecido",
      },
      {
        id: 24,
        tipo: "Outros",
        pergunta: "Trilhas",
        // resposta: inscricao?.formulario?.trilhas || "Nenhuma",
        resposta: [inscricao?.primeiraOpcaoTrilha, inscricao?.segundaOpcaoTrilha]
          .join(", ") || "Nenhuma",
      },
      {
        id: 25,
        tipo: "Outros",
        pergunta: "LinkedIn",
        resposta: inscricao?.linkedin,
      },
      {
        id: 26,
        tipo: "Outros",
        pergunta: "Github",
        resposta: inscricao?.github,
      },
      {
        id: 27,
        tipo: "Outros",
        pergunta: "LGPD",
        resposta: formatBooleanText(inscricao?.lgpd as string) ?? "Não fornecido",
      },
      {
        id: 28,
        tipo: "Outros", 
        pergunta: "Descrição Configuração PC",
        resposta: inscricao?.descConfigPc ?? "Não fornecido",
      }
    ];

    const rowsDois = [
      {
        id: 1,
        tipo: "Pessoal",
        pergunta: "Nome",
        resposta: inscricao?.candidato.nome,
      },
      {
        id: 2,
        tipo: "Pessoal",
        pergunta: "Email",
        resposta: inscricao?.candidato.email,
      },
      {
        id: 3,
        tipo: "Pessoal",
        pergunta: "Telefone",
        resposta: inscricao?.candidato.telefone,
      },
      {
        id: 4,
        tipo: "Pessoal",
        pergunta: "CPF",
        resposta: inscricao?.candidato.cpf,
      },
      {
        id: 6,
        tipo: "Pessoal",
        pergunta: "Data de Nascimento",
        resposta: inscricao?.candidato.dataNascimento,
      },
      {
        id: 7,
        tipo: "Pessoal",
        pergunta: "Estado",
        resposta: inscricao?.candidato.estado,
      },
      {
        id: 8,
        tipo: "Pessoal",
        pergunta: "Cidade",
        resposta: inscricao?.candidato.cidade,
      },
      {
        id: 9,
        tipo: "Pessoal",
        pergunta: "Neurodiversidade",
        resposta: inscricao?.candidato.neurodiversidade,
      },
      {
        id: 10,
        tipo: "Pessoal",
        pergunta: "PCD",
        resposta: inscricao?.candidato.pcd,
      },
      {
        id: 11,
        tipo: "Outros",
        pergunta: "Trilhas",
        resposta: [inscricao?.primeiraOpcaoTrilha, inscricao?.segundaOpcaoTrilha]
          .join(", "),
      },
      {
        id: 12,
        tipo: "Acadêmico",
        pergunta: "Instituição",
        resposta: inscricao?.instituicao,
      },
      {
        id: 13,
        tipo: "Acadêmico",
        pergunta: "Curso",
        resposta: inscricao?.curso,
      },
      {
        id: 14,
        tipo: "Acadêmico",
        pergunta: "Graduação",
        resposta: inscricao?.tipoGraduacao ?? "Não fornecido"
      },
      {
        id: 15,
        tipo: "Acadêmico",
        pergunta: "Turno",
        resposta: inscricao?.turno,
      },
      {
        id: 16,
        tipo: "Acadêmico",
        pergunta: "Semestre atual",
        resposta: inscricao?.semestreAtual ?? "Não fornecido"
      },
    ];

    return (
      <>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullScreen
        >
          <DialogTitle id="alert-dialog-title">
            <Button onClick={handleClose} autoFocus sx={{ mr: 2 }}>
              Fechar
            </Button>
            Informações do candidato
          </DialogTitle>
          <DialogContent>
            <Grid item sx={{ height: "calc(100vh - 90px)", width: "100%" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={rows.length}
                getRowHeight={() => "auto"}
                sx={{
                  boxShadow: 2,
                  ".MuiDataGrid-cell": {
                    padding: "20px 10px",
                  },
                }}
                hideFooter
              />
            </Grid>
          </DialogContent>
        </Dialog>
        <Grid
          container
          sx={{
            maxHeight: "calc(100vh - 180px)",
          }}
          spacing={1}
        >
          <Grid
            item
            xs={12}
            lg={6}
            sx={{ height: "calc(100vh - 160px)", width: "100%" }}
          >
            <DataGrid
              rows={rowsDois}
              columns={columns}
              onRowClick={(row) => {
                setOpen((prev) => !prev);
              }}
              pageSize={rows.length}
              sx={{
                boxShadow: 2,
              }}
              hideFooter
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Box sx={{ typography: "body1", boxShadow: 2 }}>
              <TabContext value={value}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 0.45, gap: 1 }}>
                    <TabList
                      onChange={handleChange}
                      aria-label="Abas de currículo, comprovante e etapas"
                      sx={{ p: 0 }}
                    >
                      <Tab label="Currículo" value="1" />
                      <Tab label="Comprovante de matrícula" value="2" />
                      <Tab label="Etapas" value="3" />
                    </TabList>
                    {(value === "1" || value === "2") && (
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        disabled={uploadingCurriculo || uploadingComprovante}
                      >
                        {value === "1" ? "Upload currículo" : "Upload comprovante"}
                        <input
                          hidden
                          type="file"
                          accept="application/pdf"
                          onChange={value === "1" ? handleUploadCurriculo : handleUploadComprovante}
                        />
                      </Button>
                    )}
                  </Box>
                </Box>
                <TabPanel value="1" sx={{ height: "calc(100vh - 224px)", p: 0 }}>
                  {carregandoCurriculo ? (
                    <LinearProgress/>
                  ) : (
                    formattedCandidatePdf !== "" ? (
                      <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                        <Viewer
                          fileUrl={formattedCandidatePdf}
                          plugins={[defaultLayoutPluginInstance]}
                          renderError={() => renderUploadFallback("curriculo")}
                        />
                      </Worker>
                    ) : renderUploadFallback("curriculo")
                  )}
                </TabPanel>
                <TabPanel value="2" sx={{ height: "calc(100vh - 224px)", p: 0 }}>
                  {carregandoComprovanteMatricula ? (
                    <LinearProgress/>
                  ) : (
                    comprovanteFormatadoPDF !== "" ? (
                      <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                        <Viewer
                          fileUrl={comprovanteFormatadoPDF}
                          plugins={[defaultLayoutPluginInstance]}
                          renderError={() => renderUploadFallback("comprovante")}
                        />
                      </Worker>
                    ) : renderUploadFallback("comprovante")
                  )}
                </TabPanel>
                <TabPanel value="3">
                  {inscricao?.inscricaoEtapas
                    .filter((etapa) =>
                      ["ANDAMENTO", "FINALIZADO", "APROVADO", "REPROVADO"].includes(etapa.statusEtapa)
                    )
                    .sort((a, b) => a.ordemEtapa - b.ordemEtapa)
                    .map((etapa) => (
                      <EtapaAcordeaoItem
                        key={etapa.idEtapa}
                        etapa={etapa}
                        idInscricao={inscricao.idInscricao}
                      />
                  ))}
              </TabPanel>
              </TabContext>
            </Box>
          </Grid>
        </Grid>
      </>
    );
};
